<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { IconX } from '@tabler/icons-vue'
import { Button } from '@ui'
import SidePanel from '@/ui-delta/SidePanel.vue'
import { useAnalystAvailability, type ScreenContext } from '@/api/analyst'
import { useMyFunction } from '@/api/my-function'
import { useOpportunities } from '@/api/opportunities'
import { useObjectives } from '@/api/intelligence-actions'
import { useDataHistory } from '@/api/data-history'
import { useAskElfieStore } from '@/stores/ask-elfie'
import type { WorkspaceAccess } from '@/types/api'
import { SUGGESTIONS, errorText, stageText } from '@/lib/analyst'
import { INTRODUCTIONS, companionBubbles } from '@/lib/companion'
import AnalystPromptBar from './AnalystPromptBar.vue'
import AnalystAnswer from './AnalystAnswer.vue'
import AnalystHowItWorks from './AnalystHowItWorks.vue'
import AnswerTakeaways from './AnswerTakeaways.vue'
import ElfieCompanion from './ElfieCompanion.vue'
import ElfieFace from './ElfieFace.vue'
import { useAnalystConversation } from './useAnalystConversation'

/**
 * Ask Elfie on an analysis page (P4 §C7.1): Elfie as a companion in the
 * corner, whose bubbles offer a question (Dao, 1 Oct 2026), and the panel
 * its answers open in (Modality A, §4.1), which leaves the page readable
 * beside it. The question box is at the panel's foot, with the page's
 * suggestions until the first question. The side menu, ⌘K and Elfie Pulse
 * open it too, through the Ask Elfie store.
 *
 * A question carries what the page shows (`context`, §3). Elfie writes for
 * the reader's declared function, and asks once for it while there is none
 * (§C4.1 rule 4).
 */
const props = defineProps<{ workspace: WorkspaceAccess; context: ScreenContext }>()
const { t, locale } = useI18n()

const workspaceId = computed(() => props.workspace.workspaceId)
const availability = useAnalystAvailability(workspaceId)
const { turns, busy, ask, reset } = useAnalystConversation(workspaceId)

const askElfie = useAskElfieStore()
const { open: panelOpen, pending } = storeToRefs(askElfie)
const howItWorksOpen = ref(false)
const bar = ref<InstanceType<typeof AnalystPromptBar> | null>(null)
const thread = ref<HTMLElement | null>(null)

const submit = (question: string): void => {
  const opening = !panelOpen.value
  panelOpen.value = true
  void ask(question, props.context)
  // The bar moves into the panel: the cursor goes with it, for the follow-up.
  if (opening) void nextTick(() => bar.value?.focus())
}

// A question asked for the reader from elsewhere on the page (Elfie Pulse): asked as the panel opens,
// or as soon as the answer on its way is in.
watch(
  [pending, busy],
  ([question, working]) => {
    if (question && !working) submit(askElfie.take() ?? question)
  },
  { immediate: true },
)
// One not asked yet belongs to this page and workspace: it is never asked on another.
watch(workspaceId, () => askElfie.take(), { flush: 'sync' })
onBeforeUnmount(() => askElfie.take())

// ─── The reader's function (S0 §18.0a) ──────────────────────────────────────
const myFunction = useMyFunction(workspaceId, panelOpen)
const declared = computed(() => myFunction.data.value?.function ?? null)
const ASKED = (id: string): string => `ask-elfie:function-asked:${id}`
const readAsked = (): boolean => {
  try {
    return window.localStorage.getItem(ASKED(workspaceId.value)) === '1'
  } catch {
    return false
  }
}
const askedOnce = ref(readAsked())
watch(workspaceId, () => (askedOnce.value = readAsked()))
const notNow = (): void => {
  askedOnce.value = true
  try {
    window.localStorage.setItem(ASKED(workspaceId.value), '1')
  } catch {
    // Without storage it asks again next time; nothing else depends on it.
  }
}
const settingsLink = computed(() => ({ name: 'workspace-my-function', params: { workspaceId: workspaceId.value } }))

// ─── The companion's bubbles ────────────────────────────────────────────────
const canAsk = computed(() => availability.data.value?.status === 'PERMITTED')
// The workspace's own figures, as the Opportunities and Objectives pages show them: a bubble claims nothing the data does not.
const open = useOpportunities(
  workspaceId,
  { period: 'LAST_30_DAYS', status: 'ACTIVE', domain: 'ALL', priority: 'ALL' },
  canAsk,
)
const objectives = useObjectives(workspaceId, 'ACTIVE', canAsk)
// With nothing recorded yet, there is nothing to compare or count: Elfie only introduces itself (DECISIONS.md #34).
const history = useDataHistory(workspaceId, canAsk)
const noData = computed(() => history.data.value?.stage === 'NO_DATA')
// Where Elfie cannot answer, it offers nothing: the panel says why.
const bubbles = computed(() => {
  if (!canAsk.value) return []
  const all = companionBubbles({ opportunities: open.data.value?.items ?? [], objectives: objectives.data.value ?? [] }, t, locale.value)
  return noData.value ? all.filter((bubble) => (INTRODUCTIONS as readonly string[]).includes(bubble.key)) : all
})

/** Opens the panel; with a question, asks it. */
const openWith = (question: string | null): void => {
  if (question) return askElfie.ask(question)
  askElfie.show()
  void nextTick(() => bar.value?.focus())
}

const startOver = (): void => {
  reset()
  void nextTick(() => bar.value?.focus())
}

// The newest turn in view as it grows: the stage line, then the answer. Only the panel scrolls, never the page behind it.
watch(
  () => turns.value.map((turn) => `${turn.key}:${turn.stage}:${turn.answer?.answerId ?? ''}:${turn.error?.code ?? ''}`).join(),
  () =>
    void nextTick(() => {
      const body = thread.value?.parentElement
      body?.scrollTo({ top: body.scrollHeight, behavior: 'smooth' })
    }),
)

const onKey = (event: KeyboardEvent): void => {
  if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
    event.preventDefault()
    openWith(null)
  }
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <ElfieCompanion v-if="!panelOpen" :bubbles="bubbles" @open="openWith" />

  <SidePanel v-model="panelOpen" :label="t('analyst.name')" data-test="analyst-panel">
    <template #header>
      <h2 class="panel__title">{{ t('analyst.name') }}</h2>
      <Button size="sm" type="ghost" :disabled="turns.length === 0" data-test="analyst-new" @click="startOver">
        {{ t('analyst.panel.newConversation') }}
      </Button>
      <button type="button" class="panel__close" :aria-label="t('analyst.panel.close')" @click="panelOpen = false">
        <IconX :size="18" />
      </button>
    </template>

    <p v-if="declared" class="perspective" data-test="ask-elfie-function">
      {{ t('analyst.perspective.tailored', { function: t(`myFunction.functions.${declared}.name`) }) }}
      <RouterLink :to="settingsLink" class="perspective__link">{{ t('analyst.perspective.change') }}</RouterLink>
    </p>
    <div v-else-if="myFunction.isSuccess.value && !askedOnce" class="perspective perspective--ask" data-test="ask-elfie-function-ask">
      <p class="perspective__text">{{ t('analyst.perspective.ask') }}</p>
      <div class="perspective__actions">
        <RouterLink :to="settingsLink" class="perspective__link" data-test="ask-elfie-function-set">{{ t('analyst.perspective.set') }}</RouterLink>
        <Button size="sm" type="ghost" data-test="ask-elfie-function-later" @click="notNow">{{ t('analyst.perspective.notNow') }}</Button>
      </div>
    </div>
    <p v-else-if="myFunction.isSuccess.value" class="perspective" data-test="ask-elfie-function-institution">
      {{ t('analyst.perspective.institution') }}
      <RouterLink :to="settingsLink" class="perspective__link">{{ t('analyst.perspective.set') }}</RouterLink>
    </p>

    <!-- Before the first question: Elfie, saying hello; the suggestions are in a row above the box below. -->
    <div v-if="turns.length === 0" class="hello" data-test="ask-elfie-hello">
      <span class="hello__face"><ElfieFace /></span>
      <p class="hello__text">{{ t('analyst.companion.hello') }}</p>
    </div>

    <ol ref="thread" class="thread" aria-live="polite">
      <li v-for="turn in turns" :key="turn.key" class="turn" data-test="analyst-turn">
        <p class="turn__question">
          <span class="visually-hidden">{{ t('analyst.panel.you') }}:</span>
          {{ turn.question }}
        </p>
        <p v-if="turn.stage" class="turn__stage" data-test="analyst-stage">
          <span class="turn__pulse" aria-hidden="true" />
          {{ stageText(turn, t, locale) }}
        </p>
        <template v-else-if="turn.answer">
          <AnalystAnswer :answer="turn.answer" />
          <AnswerTakeaways v-if="turn.answer.outcome === 'ANSWERED'" :workspace-id="workspaceId" :answer="turn.answer" :question="turn.question" />
        </template>
        <p v-else-if="turn.error" class="turn__error" role="alert" data-test="analyst-error">{{ errorText(turn.error, t) }}</p>
      </li>
    </ol>

    <template #footer>
      <AnalystPromptBar
        ref="bar"
        class="panel__bar"
        :placeholder="turns.length === 0 ? t('analyst.bar.placeholder', { workspace: workspace.name }) : t('analyst.bar.followUp')"
        :suggestions="turns.length === 0 ? SUGGESTIONS[context.surface] : []"
        :availability="availability.data.value ?? null"
        :busy="busy"
        @ask="submit"
        @how-it-works="howItWorksOpen = true"
      />
    </template>
  </SidePanel>

  <AnalystHowItWorks
    v-if="availability.data.value"
    :open="howItWorksOpen"
    :availability="availability.data.value"
    @close="howItWorksOpen = false"
  />
</template>

<style scoped>
.panel__title {
  flex: 1 1 auto;
  margin: 0;
  font-size: var(--font-size-body-1);
  line-height: var(--line-height-body-1);
  font-weight: var(--font-weight-semibold);
  color: var(--fg-primary);
}

.panel__close {
  display: inline-flex;
  padding: var(--spacing-xs);
  border: 0;
  border-radius: var(--radius-md);
  background: none;
  color: var(--fg-secondary);
  cursor: pointer;
}

.panel__close:hover {
  background: var(--surface-hover);
  color: var(--fg-primary);
}

.panel__close:focus-visible {
  outline: 2px solid var(--focus-ring);
}

/* Whose view Elfie answers from: the reader's declared function, or the institution's until there is one. */
.perspective {
  margin: 0 0 var(--spacing-lg);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-secondary);
}

.perspective--ask {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--radius-lg);
  background: var(--surface-sunken);
}

.perspective__text {
  margin: 0;
  color: var(--fg-primary);
}

.perspective__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-md);
}

.perspective__link {
  font-weight: var(--font-weight-medium);
  color: var(--accent);
}

.hello {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-6xl) 0 var(--spacing-2xl);
}

.hello__face {
  inline-size: var(--spacing-8xl);
  block-size: var(--spacing-8xl);
}

.hello__text {
  margin: 0;
  font-size: var(--font-size-body-1);
  line-height: var(--line-height-body-1);
  font-weight: var(--font-weight-semibold);
  color: var(--fg-primary);
}

/* Inside the panel the bar is its foot, not a floating card. */
.panel__bar {
  padding: 0;
  border: 0;
  box-shadow: none;
  background: none;
}

.thread {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xl);
  margin: 0;
  padding: 0;
  list-style: none;
}

.turn {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.turn__question {
  align-self: flex-end;
  max-width: 85%;
  margin: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-xl);
  background: var(--surface-sunken);
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  color: var(--fg-primary);
  overflow-wrap: anywhere;
}

.turn__stage {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0;
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-secondary);
}

.turn__pulse {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: var(--accent);
  animation: pulse 1.2s ease-in-out infinite;
}

@keyframes pulse {
  50% {
    opacity: 0.3;
  }
}

@media (prefers-reduced-motion: reduce) {
  .turn__pulse {
    animation: none;
  }
}

.turn__error {
  margin: 0;
  font-size: var(--font-size-body-2);
  line-height: var(--line-height-body-2);
  color: var(--fg-error-strong);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
